import { Controller } from '@hotwired/stimulus'
import { filterMenu, flattenMenuResults, rankResults } from '~/layout/menu_search'
import { clearRecentlyOpened, findActiveLeaf, readRecentlyOpened, recordRecentlyOpened } from '~/layout/recently_opened'

export default class extends Controller {
  static targets = ['input', 'menu', 'flat', 'empty', 'adminBlock']
  static values = { tree: Object, icons: Object }

  connect() {
    if (this.hasTreeValue) recordRecentlyOpened(findActiveLeaf(this.treeValue))
    this._focused = false
    this._onKeydown = (e) => {
      if (e.key === 'Escape' && this.hasInputTarget && this.inputTarget.value) {
        this.inputTarget.value = ''
        this.inputTarget.blur()
        this._render()
      }
    }
    document.addEventListener('keydown', this._onKeydown)
    this._render()
  }

  disconnect() {
    document.removeEventListener('keydown', this._onKeydown)
    clearTimeout(this._blurTimer)
  }

  focusInput() {
    clearTimeout(this._blurTimer)
    this._focused = true
    this._render()
  }

  blurInput() {
    this._blurTimer = setTimeout(() => {
      this._focused = false
      this._render()
    }, 150)
  }

  filter() {
    this._render()
  }

  clear(e) {
    if (e) e.stopPropagation()
    clearRecentlyOpened()
    if (this.hasInputTarget) this.inputTarget.focus()
    this._render()
  }

  _render() {
    const query = this.hasInputTarget ? this.inputTarget.value.trim() : ''
    if (query) {
      const results = this.hasTreeValue ? rankResults(flattenMenuResults(filterMenu(this.treeValue, query)), query) : []
      this._renderFlat(results, {})
      this._setSearching(true, query)
      if (this.hasEmptyTarget) this.emptyTarget.hidden = results.length > 0
    } else if (this._focused) {
      const recent = readRecentlyOpened()
      if (recent.length) {
        this._renderFlat(recent, { label: 'Recently opened', clearable: true })
        this._setSearching(true, query)
      } else {
        this._setSearching(false, query)
      }
      if (this.hasEmptyTarget) this.emptyTarget.hidden = true
    } else {
      this._setSearching(false, query)
      if (this.hasEmptyTarget) this.emptyTarget.hidden = true
    }
  }

  _setSearching(showFlat, query) {
    if (this.hasFlatTarget) this.flatTarget.hidden = !showFlat
    this.menuTargets.forEach((m) => {
      m.hidden = showFlat
    })
    if (this.hasAdminBlockTarget) this.adminBlockTarget.hidden = !!query
  }

  _renderFlat(items, { label = null, clearable = false }) {
    if (!this.hasFlatTarget) return
    this.flatTarget.replaceChildren()

    if (label) {
      const header = document.createElement('div')
      header.className = 'menu-flat-header sidebar-section-header'
      const title = document.createElement('span')
      title.textContent = label
      header.appendChild(title)
      if (clearable) {
        const clearBtn = document.createElement('button')
        clearBtn.type = 'button'
        clearBtn.className = 'menu-flat-clear'
        clearBtn.textContent = 'Clear'
        clearBtn.addEventListener('click', (e) => this.clear(e))
        header.appendChild(clearBtn)
      }
      this.flatTarget.appendChild(header)
    }

    for (const item of items) {
      const a = document.createElement('a')
      a.className = 'menu-flat-item'
      a.href = item.href

      const icon = document.createElement('span')
      icon.className = 'menu-flat-item-icon'
      const svg = this.hasIconsValue ? this.iconsValue[item.icon] : null
      if (svg) icon.innerHTML = svg
      a.appendChild(icon)

      const body = document.createElement('span')
      body.className = 'menu-flat-item-body'
      const labelEl = document.createElement('span')
      labelEl.className = 'menu-flat-item-label'
      labelEl.textContent = item.label
      body.appendChild(labelEl)
      const path = [item.section, item.groupLabel].filter(Boolean).join(' › ')
      if (path) {
        const pathEl = document.createElement('span')
        pathEl.className = 'menu-flat-item-path'
        pathEl.textContent = path
        body.appendChild(pathEl)
      }
      a.appendChild(body)
      this.flatTarget.appendChild(a)
    }
  }
}
